import { type Chart } from "chart.js";
import {
  type AnnotationOptions,
  type LabelPositionObject,
} from "chartjs-plugin-annotation";
import moment from "moment";

import { makeFont } from "@features/mmm/components/model-insights/model-fit-chart/utils/annotation-helpers";

import { getChartData } from "./chart-data-manager-plugin";

export const todayMarkerAnnotation = (
  timezone: string,
): Record<string, AnnotationOptions> => {
  return {
    todayLabel: {
      type: "label",
      display: (ctx) => getXPosition(ctx.chart, timezone) > -1,
      position: (ctx) => getLabelPosition(ctx.chart, timezone),
      xValue: (ctx) => getXPosition(ctx.chart, timezone),
      yValue: (ctx) => ctx.chart.scales.y.max,
      content: "TODAY",
      font: makeFont({
        size: 12,
        weight: "bold",
      }),
    },
    todayDateLabel: {
      type: "label",
      display: (ctx) => getXPosition(ctx.chart, timezone) > -1,
      position: (ctx) => getLabelPosition(ctx.chart, timezone),
      xValue: (ctx) => getXPosition(ctx.chart, timezone),
      yValue: (ctx) => ctx.chart.scales.y.max,
      yAdjust: 18,
      xAdjust: (ctx) => getXLabelAdjust(ctx.chart, timezone),
      content: () => moment.tz(timezone).format("MMM DD"),
      font: makeFont({
        size: 10,
        weight: "normal",
      }),
    },
    todayLine: {
      display: (ctx) => getXPosition(ctx.chart, timezone) > -1,
      yScaleID: "y",
      xScaleID: "x",
      borderColor: "rgba(0, 0, 0, 0.6)",
      borderDash: [3, 3],
      type: "line",
      xMin: (ctx) => getXPosition(ctx.chart, timezone),
      xMax: (ctx) => getXPosition(ctx.chart, timezone),
      yMin: (ctx) => ctx.chart.scales.y.min,
    },
  };
};

function getXPosition(chart: Chart, timezone: string) {
  const chartData = getChartData(chart);

  if (!chartData) {
    return 0;
  }

  const today = moment().tz(timezone).format("MMM DD, YYYY");
  const position = chartData.data.labels.findIndex((e) => e === today);
  return position;
}

function getLabelPosition(chart: Chart, timezone: string): LabelPositionObject {
  const position = getXPosition(chart, timezone);
  const chartData = getChartData(chart);
  const max = chartData?.data.labels.length ?? 0;

  if (position > max - 5) {
    return {
      x: "end",
      y: "start",
    };
  } else {
    return {
      x: "start",
      y: "start",
    };
  }
}

function getXLabelAdjust(chart: Chart, timezone: string) {
  const position = getXPosition(chart, timezone);
  const chartData = getChartData(chart);
  const max = chartData?.data.labels.length ?? 0;

  return position > max - 5 ? -3 : 3;
}
